// M012 Phase 12.4a: tabbed middle column. The tab bar sits flush
// under the smoked-glass chrome; active tab gets an underlined
// accent marker; inactive tabs muted. Tab state lives in the URL
// search param `?tab=…` so deep-linking works.
//
// Captain 2026-04-23: the top-right theme + user icons live INSIDE
// the tab bar so they move with it when the drawer opens. They
// render as right-justified bottom-aligned items with the same
// rhythm as the tab labels.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { ThemeIcon } from "../../theme/ThemeIcon";
import { UserPopout } from "../../layout/UserPopout";
import { SandboxStatusPill } from "../SandboxStatusPill";

export type TabId =
  | "sandbox"
  | "operator"
  | "chat"
  | "road"
  | "files"
  | "telemetry"
  | "personas"
  | "recipes"
  | "receipts"
  | "newsfeed"
  | "models"
  | "usage"
  | "settings"
  | "status";

interface TabDef {
  id: TabId;
  label: string;
  /** When set, the tab is hidden unless the entitlement flag is true. */
  entitlementGate?: "road_view_visible";
}

// A33 (canon update 2026-04-26): Sandbox is the default tab. The user
// watches the working agent operate inside the sandbox in real time.
// A34: chat tab retired — the drawer IS the canonical team chat.
// A06: Files tab — sandbox file tree + Monaco viewer.
// A09 Captain directive: Ledger is the Road's decoration — merged into
// the Road tab as a Curated/Raw toggle pane. Separate Ledger tab gone.
// A40: Road tab is premium-gated via `road_view_visible`.
const TABS: TabDef[] = [
  { id: "sandbox", label: "Sandbox" },
  { id: "operator", label: "Operations" },
  { id: "road", label: "Road", entitlementGate: "road_view_visible" },
  { id: "files", label: "Files" },
  { id: "telemetry", label: "Telemetry" },
  { id: "personas", label: "Personas" },
  { id: "recipes", label: "Recipes" },
  { id: "receipts", label: "Receipts" },
  { id: "newsfeed", label: "Newsfeed" },
  { id: "models", label: "Models" },
  { id: "usage", label: "Usage" },
  { id: "settings", label: "Settings" },
  { id: "status", label: "Status" },
];

export function useActiveTab(): TabId {
  const [params] = useSearchParams();
  const raw = params.get("tab") ?? "sandbox";
  if (TABS.some((t) => t.id === raw)) return raw as TabId;
  return "sandbox";
}

interface TabBarProps {
  /** Retained for call-site compatibility; the chrome no longer needs
   * an org-scoped balance widget (council/Models tab use the staff
   * key). Reserved for future workspace-budget chip. */
  orgId?: number | null;
  workspaceId?: number;
  entitlements?: {
    road_view_visible: boolean;
  } | null;
}

export function TabBar({
  orgId: _orgId = null,
  workspaceId = 0,
  entitlements = null,
}: TabBarProps = {}) {
  const [params, setParams] = useSearchParams();
  const active = useActiveTab();

  function onPick(id: TabId) {
    const next = new URLSearchParams(params);
    if (id === "sandbox") {
      next.delete("tab");
    } else {
      next.set("tab", id);
    }
    setParams(next, { replace: true });
  }

  // Captain 2026-04-26: tab labels never scroll. When the page is too
  // narrow to fit them all, overflowing tabs collapse into a "…" menu
  // anchored to the right of the visible row. The right-side chrome
  // (Sandbox pill, balance, theme, user popout) stays anchored — it
  // never participates in overflow.
  //
  // Measurement strategy: every tab is rendered twice — once visibly
  // in the strip, once in an off-screen mirror that keeps the natural
  // width readable even when the visible copy is hidden by overflow.
  // ResizeObserver on the strip recomputes visibleCount from the
  // mirror widths, so widening the page restores tabs that were
  // previously hidden (the bug in the first revision).
  // A40: filter TABS by entitlement gate. When a gate is named on a tab
  // and the entitlement isn't granted, the tab is removed from every
  // surface (visible strip, mirror, overflow menu).
  const availableTabs = useMemo(() => {
    return TABS.filter((tab) => {
      if (!tab.entitlementGate) return true;
      if (tab.entitlementGate === "road_view_visible") {
        return Boolean(entitlements?.road_view_visible);
      }
      return true;
    });
  }, [entitlements]);

  const stripRef = useRef<HTMLDivElement>(null);
  const mirrorRefs = useRef<Map<TabId, HTMLSpanElement>>(new Map());
  const [visibleCount, setVisibleCount] = useState(availableTabs.length);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowMenuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const measure = () => {
      const stripWidth = strip.clientWidth;
      // Reserve room for the trailing overflow trigger (≈ 2rem incl.
      // gap) so the "…" never paints over the last visible tab.
      const reserve = 28;
      const gap = 16; // tailwind gap-4
      let used = 0;
      let count = 0;
      for (const tab of availableTabs) {
        const el = mirrorRefs.current.get(tab.id);
        if (!el) continue;
        const w = el.offsetWidth + (count === 0 ? 0 : gap);
        if (used + w + reserve <= stripWidth) {
          used += w;
          count += 1;
        } else {
          break;
        }
      }
      if (count >= availableTabs.length) setVisibleCount(availableTabs.length);
      else if (count === 0) setVisibleCount(1);
      else setVisibleCount(count);
    };
    const ro = new ResizeObserver(() => measure());
    ro.observe(strip);
    measure();
    return () => ro.disconnect();
  }, [availableTabs]);

  useEffect(() => {
    if (!overflowOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (!overflowMenuRef.current?.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOverflowOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [overflowOpen]);

  const overflowTabs = useMemo(
    () => availableTabs.slice(visibleCount),
    [availableTabs, visibleCount],
  );
  const showOverflow = overflowTabs.length > 0;
  const activeInOverflow = overflowTabs.some((t) => t.id === active);

  const visibleTabs = availableTabs.slice(0, visibleCount);
  return (
    <nav
      className="absolute left-0 right-0 top-0 z-20 flex h-14 items-end gap-4 overflow-visible bg-transparent px-5 pb-2"
      data-testid="tab-bar"
      role="tablist"
    >
      {/* Off-screen mirror — same font + spacing as visible tabs so
          offsetWidths match. Pointer-events off and aria-hidden so it
          never participates in input or assistive tech. */}
      <div
        aria-hidden
        className="pointer-events-none invisible fixed left-[-9999px] top-0 flex gap-4 text-xs font-medium"
      >
        {availableTabs.map((t) => (
          <span
            key={`mirror-${t.id}`}
            ref={(el) => {
              if (el) mirrorRefs.current.set(t.id, el);
              else mirrorRefs.current.delete(t.id);
            }}
          >
            {t.label}
          </span>
        ))}
      </div>
      <div
        ref={stripRef}
        className="flex min-w-0 flex-1 items-end gap-4 overflow-hidden"
      >
        {visibleTabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onPick(t.id)}
              role="tab"
              aria-selected={isActive}
              data-testid={`tab-${t.id}`}
              className={`relative shrink-0 cursor-pointer text-xs font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted hover:text-primary"
              }`}
            >
              {t.label}
              {isActive && (
                <span
                  aria-hidden
                  className="absolute -bottom-2 left-0 right-0 h-px bg-accent"
                />
              )}
            </button>
          );
        })}
      </div>
      {showOverflow && (
        <div className="relative shrink-0 self-end pb-0" ref={overflowMenuRef}>
          <button
            type="button"
            onClick={() => setOverflowOpen((v) => !v)}
            className={`relative cursor-pointer text-xs font-medium transition-colors ${
              activeInOverflow || overflowOpen
                ? "text-primary"
                : "text-muted hover:text-primary"
            }`}
            data-testid="tab-overflow-toggle"
            title="More tabs"
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
          >
            …
            {activeInOverflow && (
              <span
                aria-hidden
                className="absolute -bottom-2 left-0 right-0 h-px bg-accent"
              />
            )}
          </button>
          {overflowOpen && (
            <div
              className="absolute right-0 top-full z-50 mt-2 w-44 rounded-md border border-edge bg-surface-raised p-1 shadow-[var(--wt-shadow)]"
              role="menu"
              data-testid="tab-overflow-menu"
            >
              {overflowTabs.map((t) => {
                const isActive = active === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      onPick(t.id);
                      setOverflowOpen(false);
                    }}
                    role="menuitem"
                    data-testid={`tab-overflow-${t.id}`}
                    className={`flex w-full cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-left text-xs transition-colors ${
                      isActive
                        ? "bg-accent/10 text-accent"
                        : "text-muted hover:bg-surface hover:text-primary"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div className="flex shrink-0 items-end gap-4 text-xs font-medium leading-none">
        {workspaceId > 0 && <SandboxStatusPill workspaceId={workspaceId} />}
        <ThemeIcon />
        <UserPopout />
      </div>
    </nav>
  );
}

// Exported for NavLink-based alternatives if we ever move to path-
// segmented routing (/projects/:slug/channels/:channel/:tab).
export { NavLink };
